import React, { useState, useEffect } from 'react';
import './property.scss';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import PropertyAddressForm from './propertyAddress';
import HomeIcon from '@mui/icons-material/Home';
import LandDetails from './landDetails';
import BuildingDetails from './buildingDetails';
import PropertyImages from './propertyImages';
import PropertyTax from './propertyTax';
import CommercialDetails from './commercialDetails';
import ContactDetails from './contactDetails';
import UploadDocuments from './uploadDocumnets';
import { addPropertyDetail } from '../../Api/request/propertyDetail';
import { fetchPropertyType } from '../../Api/request/propertyMasterApi';
import UseAxios from '../../Api/http/http';
import PropertyLayout from './layout';
import ReviewPropertyDetails from './reviewPropertyDetails';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';
import { useLocation, useNavigate } from 'react-router-dom';

const PROPERTY_SUBTYPES = {
  Residential: [
    { id: 'Flat', name: 'Flat' },
    { id: 'Villa', name: 'Villa' },
    { id: 'Plot', name: 'Plot' },
  ],
  Commercial: [
    { id: 'Complex', name: 'Complex' },
    { id: 'Shop', name: 'Shop' },
    { id: 'Plot', name: 'Plot' },
  ],
  Agricultural: [
    { id: 'Land', name: 'Land' },
    { id: 'Plot', name: 'Plot' },
  ],
};

const initialPropertyState = {
  caption: '',
  type: '',
  subType: '',
  description: '',
};

function PropertyForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const editData = location.state?.propertyData;
  const isEdit = location.state?.isEdit;
  const isView = location.state?.isView;
  const editPropertyId = location.state?.property_id;
  const initialStep = location.state?.initialStep || 1;

  const [form, setForm] = useState(initialPropertyState);
  const [step, setStep] = useState(initialStep);
  const [propertyData, setPropertyData] = useState({});
  const [PROPERTY_TYPE, setPropertyType] = useState([]);
  const [errors, setErrors] = useState({});
  const [systemId, setSystemId] = useState(editPropertyId || ''); // Will be set from API response or edit data

  // Use the API state hook
  const {
    isLoading,
    isSubmitting,
    error,
    submitError,
    clearErrors,
    fetchData,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to process request',
  });

  // Handle back to dashboard navigation
  const handleBackToDashboard = () => {
    navigate('/property-list');
  };

  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, type: event.target.value.name, subType: '' }));
    setErrors((prev) => ({ ...prev, type: undefined }));
  };

  const handleSubTypeChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, subType: event.target.value.name }));
    setErrors((prev) => ({ ...prev, subType: undefined }));
  };

  const handleNext = async (event) => {
    event.preventDefault();

    // In view mode, just navigate without validation or saving
    if (isView) {
      setPropertyData((prev) => ({ ...prev, ...form }));
      setStep(2);
      return;
    }

    // Validation for property type and sub type
    const newErrors = {};
    if (!form.type) newErrors.type = 'Property Type is required';
    if (!form.subType) newErrors.subType = 'Property Sub-Type is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    clearErrors();
    setPropertyData((prev) => ({ ...prev, ...form }));
    const payload = systemId
      ? { ...form, property_id: systemId, ...getManagePropertySaveFields() }
      : { ...form, ...getManagePropertySaveFields() };

    try {
      const response = await submitData(() => UseAxios(addPropertyDetail(payload)), {
        successMessage: 'Basic information saved successfully',
      });

      if (response && response.data && response.data.property_id) {
        setSystemId(response.data.property_id);
      }
      console.log('response here', response);
      setStep(2);
    } catch (error) {
      // Error is already handled by useApiState
      console.error('Failed to save basic information:', error);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const getManagePropertySaveFields = () => ({
    status: 'Managing',
    currentAvailability: 'Draft',
    current_availability: 'Draft',
  });

  const handleNextClick = async (data) => {
    // In view mode, just navigate without saving
    if (isView) {
      setPropertyData((prev) => ({ ...prev, ...data }));
      setStep(step + 1);
      return;
    }

    // Clean up land/building fields if not needed in the next step
    let cleanedData = { ...data };
    // If next step is BuildingDetails, clear land fields
    if (
      form.subType !== 'Land' && form.subType !== 'Plot' && form.subType !== 'Villa' && form.subType !== 'Complex'
    ) {
      cleanedData.extent = null;
      cleanedData.unit = '';
      cleanedData.pattaExtent = null;
      cleanedData.kumkiExtent = null;
      cleanedData.boundaryType = '';
      cleanedData.roadAccess = null;
      cleanedData.roadType = '';
      cleanedData.accessRoadWidth = null;
      cleanedData.facing = [];
      cleanedData.waterSource = [];
      cleanedData.powerSupply = null;
      cleanedData.sanctionedCapacity = null;
      cleanedData.sewageLine = null;
      cleanedData.sewageType = '';
    }
    // If next step is LandDetails, clear building fields
    if (
      form.subType === 'Land' || form.subType === 'Plot'
    ) {
      cleanedData.constructionStage = '';
      cleanedData.carpetArea = null;
      cleanedData.builtUpSanctioned = null;
      cleanedData.builtUpActual = null;
      cleanedData.noOfFloors = null;
      cleanedData.noOfParking = null;
    }

    clearErrors();
    setPropertyData((prev) => ({ ...prev, ...cleanedData }));
    const payload = systemId
      ? { ...cleanedData, property_id: systemId, ...getManagePropertySaveFields() }
      : { ...cleanedData, ...getManagePropertySaveFields() };

    try {
      const response = await submitData(() => UseAxios(addPropertyDetail(payload)), {
        successMessage: 'Step data saved successfully',
      });

      if (response && response.data && response.data.property_id) {
        setSystemId(response.data.property_id);
      }
      setStep(step + 1);
    } catch (error) {
      // Error is already handled by useApiState
      console.error('Failed to save step data:', error);
    }
  };

  const fetchPropertyTypes = async () => {
    try {
      const response = await fetchData(() => UseAxios(fetchPropertyType()), {
        errorMessage: 'Failed to load property types',
      });
      setPropertyType(response.data.propertyTypes || []);
      console.log('response here', response);
    } catch (error) {
      // Error is already handled by useApiState
      console.error('Failed to fetch property types:', error);
    }
  };

  useEffect(() => {
    fetchPropertyTypes();
  }, []);

  // Populate form with edit data if editing or viewing an existing property
  useEffect(() => {
    if ((isEdit || isView) && editData) {
      console.log('Loading property data for editing/viewing:', editData);
      console.log('isEdit:', isEdit, 'isView:', isView);

      // Map the fetched property data to the form structure
      const mappedFormData = {
        caption: editData.property_caption || '',
        type: editData.property_type || '',
        subType: editData.property_sub_type || '',
        description: editData.property_description || '',
      };

      console.log('Mapped form data:', mappedFormData);
      setForm(mappedFormData);

      // Map the complete property data for all form steps
      const mappedPropertyData = {
        // Basic Information (Step 1)
        caption: editData.property_caption || '',
        type: editData.property_type || '',
        subType: editData.property_sub_type || '',
        description: editData.property_description || '',

        // Address Details (Step 2)
        address1: editData.address_line1 || '',
        address2: editData.address_line2 || '',
        area: editData.area || '',
        taluk: editData.taluk || '',
        district: editData.district || '',
        pin: editData.postal_code || '',
        city: editData.city || '',
        state: editData.state || '',
        country: editData.country || '',
        addressDescription: editData.address_description || '',
        location: editData.location || { mapLink: '', latitude: '', longitude: '' },

        // Property Details
        jurisdiction: editData.jurisdiction || '',
        constructionStage: editData.construction_stage || '',
        carpetArea: editData.carpet_area || null,
        builtUpSanctioned: editData.builtup_area_sanctioned || null,
        builtUpActual: editData.builtup_area_actual || null,
        noOfFloors: editData.floor_count || null,
        noOfParking: editData.parking_count || null,

        // Land Details
        extent: editData.land_extent ? parseFloat(editData.land_extent) : null,
        unit: editData.unit || '',
        pattaExtent: editData.patta_land_extent ? parseFloat(editData.patta_land_extent) : null,
        kumkiExtent: editData.kumki_land_extent ? parseFloat(editData.kumki_land_extent) : null,
        boundaryType: editData.boundary_type || '',
        roadAccess: editData.road_access === '1' || editData.road_access === true,
        roadType: editData.road_type || '',
        accessRoadWidth: editData.access_road_width ? parseFloat(editData.access_road_width) : null,

        // Utilities and Features
        facing: editData.site_facing_direction ? JSON.parse(editData.site_facing_direction || '[]') : [],
        waterSource: editData.water_source ? JSON.parse(editData.water_source || '[]') : [],
        powerSupply: editData.power_supply === '1' || editData.power_supply === true,
        sanctionedCapacity: editData.sanctioned_capacity ? parseFloat(editData.sanctioned_capacity) : null,
        sewageLine: editData.sewage_line === '1' || editData.sewage_line === true,
        sewageType: editData.sewage_type || '',

        // Tax and Pricing
        taxPaidUpTo: editData.tax_paid_upto || null,
        price: editData.purchase_price || null,
        purchaseDate: editData.purchase_date || null,
        estimatedValue: editData.estimated_current_value || null,

        // Contact Details
        contactName: editData.owner_name || null,
        phoneNumber: editData.owner_phone_number || null,
        email: editData.owner_email_id || null,
        caretakerIsOwner: editData.caretaker_is_owner === '1' || editData.caretaker_is_owner === true,
        caretakerName: editData.caretaker_name || null,
        caretakerPhoneNumber: editData.caretaker_phone_number || null,
        caretakerEmail: editData.caretaker_email_id || null,

        // Status and Listing
        status: editData.status || 'Draft',
        listingType: editData.listing_type || '',

        // Property Images and Documents (map to expected field names)
        images: editData.property_images || [],
        videos: editData.property_videos || [],
        receipts: editData.property_documents?.tax_receipt || [],

        // Raw data for internal use
        property_images: editData.property_images || [],
        property_documents: editData.property_documents || {},
        property_videos: editData.property_videos || [],

        // Document fields for review page
        'Tax Receipt': editData.property_documents?.tax_receipt || [],
        'Sale Deed': editData.property_documents?.sale_deed || [],
        'e-Khata': editData.property_documents?.khata || [],
        'FMB / Nakshe': editData.property_documents?.fmb_nakshe || [],
        'Conversion Order/Copy': editData.property_documents?.conversion_order_copy || [],
        'EC': editData.property_documents?.ec || [],
        'RTC': editData.property_documents?.rtc || [],
        'Mutation': editData.property_documents?.mutation || [],
        'Occupancy Certificate': editData.property_documents?.occupancy_certificate || [],
        'Original Mother Deed': editData.property_documents?.original_mother_deed || [],
        'Building Licence': editData.property_documents?.building_licence || [],
        'CRZ NOC': editData.property_documents?.crz_noc || [],
        'RERA Certificate': editData.property_documents?.rera_certificate || [],
        'Pollution Control Board': editData.property_documents?.pollution_control_board || [],
        'Fire Dept Noc': editData.property_documents?.fire_dept_noc || [],
        'Airport Clearance': editData.property_documents?.airport_clearance || [],
        'Sanction Plan Of Building': editData.property_documents?.sanction_plan_building || [],
        'Bank documents (if loans)': editData.property_documents?.bank_loan_documents || [],
        'Legal documents (if litigation)': editData.property_documents?.legal_documents || [],
        'Electricity Bill': editData.property_documents?.electricity_bill || [],
        'Rent agreements': editData.property_documents?.rent_agreements || [],
        'Survey settlement records': editData.property_documents?.survey_settlement_records || [],
        'Possession certificate': editData.property_documents?.possession_certificate || [],
        'NOC from society builder': editData.property_documents?.noc_from_society_builder || [],
        'Insurance documents': editData.property_documents?.insurance_documents || [],

        // Document existence flags
        taxReceiptExist: editData.property_documents?.tax_receipt?.length > 0,
        rTCExist: editData.property_documents?.rtc?.length > 0,
        saleDeedExist: editData.property_documents?.sale_deed?.length > 0,
        eKhataExist: editData.property_documents?.khata?.length > 0,
        fMBNaksheExist: editData.property_documents?.fmb_nakshe?.length > 0,
        conversionOrderCopyExist: editData.property_documents?.conversion_order_copy?.length > 0,
        eCExist: editData.property_documents?.ec?.length > 0,
        mutationExist: editData.property_documents?.mutation?.length > 0,
        occupancyCertificateExist: editData.property_documents?.occupancy_certificate?.length > 0,
        originalMotherDeedExist: editData.property_documents?.original_mother_deed?.length > 0,
        buildingLicenceExist: editData.property_documents?.building_licence?.length > 0,
        cRZNOCExist: editData.property_documents?.crz_noc?.length > 0,
        rERACertificateExist: editData.property_documents?.rera_certificate?.length > 0,
        pollutionControlBoardExist: editData.property_documents?.pollution_control_board?.length > 0,
        fireDeptNocExist: editData.property_documents?.fire_dept_noc?.length > 0,
        airportClearanceExist: editData.property_documents?.airport_clearance?.length > 0,
        sanctionPlanOfBuildingExist: editData.property_documents?.sanction_plan_building?.length > 0,
        bankDocumentsifLoansExist: editData.property_documents?.bank_loan_documents?.length > 0,
        legalDocumentsifLitigationExist: editData.property_documents?.legal_documents?.length > 0,
        electricityBillExist: editData.property_documents?.electricity_bill?.length > 0,
        rentAgreementsExist: editData.property_documents?.rent_agreements?.length > 0,
        surveySettlementRecordsExist: editData.property_documents?.survey_settlement_records?.length > 0,
        possessionCertificateExist: editData.property_documents?.possession_certificate?.length > 0,
        nOCFromSocietyBuilderExist: editData.property_documents?.noc_from_society_builder?.length > 0,
        insuranceDocumentsExist: editData.property_documents?.insurance_documents?.length > 0,
      };

      // Set the complete property data for all steps
      setPropertyData(mappedPropertyData);

      console.log('Form populated with edit/view data:', mappedFormData);
      console.log('Property data populated with complete edit/view data:', mappedPropertyData);
      console.log('Available PROPERTY_TYPE:', PROPERTY_TYPE);
    }
  }, [isEdit, isView, editData, PROPERTY_TYPE]); // Add PROPERTY_TYPE as dependency

  // Determine totalSteps based on flow
  let computedTotalSteps = 10;
  console.log('form here', form);
  if (form.subType === 'Land' || form.subType === 'Plot') {
    computedTotalSteps = 9;
  } else if ((form.subType) && !(form.subType === 'Villa' || form.subType === 'Complex')) {
    computedTotalSteps = 9;
  }

  // Save current step data without navigation
  const handleSaveStepData = (data) => {
    setPropertyData((prev) => ({ ...prev, ...data }));
  };

  // Final submission function for review page
  const handleFinalSubmit = async (status = 'Submitted') => {
    try {
      const normalizedStatus = String(status || '').toLowerCase();
      const finalAvailability = normalizedStatus === 'submitted' ? 'Submitted' : 'Draft';
      // Prepare final payload with all property data
      const finalPayload = {
        ...propertyData,
        property_id: systemId,
        status: 'Managing',
        currentAvailability: finalAvailability,
        current_availability: finalAvailability,
      };
      // Call the API to save the property
      const response = await UseAxios(addPropertyDetail(finalPayload));
      return response;
    } catch (error) {
      console.error('Final submit error:', error);
      throw error;
    }
  };

  // Helper to get the correct component for each step
  const getStepComponent = () => {
    const commonProps = {
      prevStep: handlePrev,
      onNext: handleNextClick,
      onSave: handleSaveStepData, // <-- new prop for saving data only
      onSubmit: handleFinalSubmit, // <-- add final submit function
      onBackToDashboard: handleBackToDashboard, // <-- add back to dashboard function
      initialValues: propertyData,
      systemId,
      totalSteps: computedTotalSteps,
      subType: form.subType, // Pass subType to child components
      isEdit, // Pass edit mode to child components
      isView, // Pass view mode to child components
      editPropertyId, // Pass property ID for edit mode
    };
    if (step === 2) {
      return <PropertyAddressForm {...commonProps} step={2} />;
    }
    if (step === 3) {
      if (form.subType === 'Land' || form.subType === 'Plot' || form.subType === 'Villa' || form.subType === 'Complex') {
        return <LandDetails {...commonProps} step={3} />;
      } else {
        return <BuildingDetails {...commonProps} step={3} />;
      }
    }
    if (step === 4) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <BuildingDetails {...commonProps} step={4} />;
      } else {
        return <PropertyImages {...commonProps} step={4} />;
      }
    }
    if (step === 5) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <PropertyImages {...commonProps} step={5} />;
      } else {
        return <PropertyTax {...commonProps} step={5} />;
      }
    }
    if (step === 6) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <PropertyTax {...commonProps} step={6} />;
      } else {
        return <UploadDocuments {...commonProps} step={6} />;
      }
    }
    if (step === 7) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <UploadDocuments {...commonProps} step={7} />;
      } else {
        return <CommercialDetails {...commonProps} step={7} />;
      }
    }
    if (step === 8) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <CommercialDetails {...commonProps} step={8} />;
      } else {
        return <ContactDetails {...commonProps} step={8} />;
      }
    }
    if (step === 9) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <ContactDetails {...commonProps} step={9} />;
      } else {
        return <ReviewPropertyDetails {...commonProps} propertyData={propertyData} step={9} />;
      }
    }
    if (step === 10) {
      if (form.subType === 'Villa' || form.subType === 'Complex') {
        return <ReviewPropertyDetails {...commonProps} propertyData={propertyData} step={10} />;
      }
    }
  };

  return (
    <>
      <LoaderOverlay isVisible={isLoading} message="Loading property types..." />

      {step === 1 && (
        <PropertyLayout
          step={1}
          totalSteps={computedTotalSteps}
          systemId={systemId}
          onBackToDashboard={handleBackToDashboard}
        >
          <div className='card'>
            <h3 className='card-title'>
              <HomeIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              {isEdit || isView ? 'Edit Property - Basic Information' : 'Basic Information'}
              {(isEdit || isView) && systemId && (
                <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
                  (ID: {systemId})
                </span>
              )}
            </h3>

            {/* Display API errors */}
            {error && (
              <div className="error-message" style={{
                padding: '10px',
                marginBottom: '15px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '4px',
                border: '1px solid #ffcdd2',
              }}>
                {error}
              </div>
            )}

            {submitError && (
              <div className="error-message" style={{
                padding: '10px',
                marginBottom: '15px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '4px',
                border: '1px solid #ffcdd2',
              }}>
                {submitError}
              </div>
            )}

            <form onSubmit={handleNext}>
              <div className='form-group'>
                <label>Property Caption</label>
                <input
                  type='text'
                  name='caption'
                  value={form.caption}
                  onChange={handleChange}
                  placeholder='Enter property caption'
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className='form-row'>
                <div className='form-group'>
                  <SearchDropdown
                    label='Property Type *'
                    options={PROPERTY_TYPE}
                    value={
                      PROPERTY_TYPE && form.type
                        ? PROPERTY_TYPE.find((item) => item.name === form.type) || ''
                        : ''
                    }
                    onChange={handleTypeChange}
                    placeholder='Select Property Type'
                    searchBox={false}
                    disabled={isSubmitting || isView}
                  />
                  {errors.type && <div className='field-note-error'>{errors.type}</div>}
                </div>
                <div className='form-group'>
                  <SearchDropdown
                    label='Property Sub-Type *'
                    options={PROPERTY_SUBTYPES[form.type] || []}
                    value={form.type && form.subType
                      ? (PROPERTY_SUBTYPES[form.type] || []).find(
                        (st) => st.name === form.subType,
                      ) || ''
                      : ''
                    }
                    onChange={handleSubTypeChange}
                    placeholder='Select Property Sub-Type'
                    searchBox={false}
                    disabled={
                      isSubmitting || isView || !form.type || (PROPERTY_SUBTYPES[form.type] || []).length === 0
                    }
                  />
                  {errors.subType && <div className='field-note-error'>{errors.subType}</div>}
                </div>
              </div>
              <div className='form-group'>
                <label>Property Description</label>
                <textarea
                  name='description'
                  value={form.description}
                  onChange={handleChange}
                  placeholder='Write about the property...'
                  disabled={isSubmitting || isView}
                  readOnly={isView}
                />
              </div>
              <div className='form-actions'>
                <button type='button' className='btn secondary' disabled>
                  Previous
                </button>
                {isView ? (
                  <button
                    type='button'
                    className='btn primary'
                    onClick={() => setStep(2)}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type='submit'
                    className='btn primary'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Next'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </PropertyLayout>
      )}
      {step > 1 && getStepComponent()}
    </>
  );
}
export default PropertyForm;
